import {
  BriefDeliverableTaskTypeEnum,
  UploaderType,
} from "@entities/brief/lib/constants";
import { useCreateBriefConfigTask } from "@entities/brief/lib/hooks/useCreateBriefConfigTask";
import { useDeleteBriefConfigTask } from "@entities/brief/lib/hooks/useDeleteBriefConfigTask";
import { useEditBriefConfigTask } from "@entities/brief/lib/hooks/useEditBriefConfigTask";
import { useGetBriefDeliverableConfig } from "@entities/brief/lib/hooks/useGetBriefDeliverableConfig";
import { useRenameBriefConfigDeliverable } from "@entities/brief/lib/hooks/useRenameBriefConfigDeliverable";
import { useReorderBriefConfigTask } from "@entities/brief/lib/hooks/useReorderBriefConfigTask";
import { useSaveDeliverableConfig } from "@entities/brief/lib/hooks/useSaveDeliverableConfig";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import {
  ApprovalType,
  IBriefDeliverableTaskConfig,
} from "@shared/lib/types/briefConfig";
import { Button, Spin, Switch, Tooltip, Typography } from "antd";
import {
  FileText,
  Plus,
  Radio,
  ScrollText,
  Trash2,
  UserCheck,
  Video,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./CampaignTimeline.module.scss";

const { Text } = Typography;

const STAGE_META: Record<
  string,
  { label: string; description: string; type: BriefDeliverableTaskTypeEnum }
> = {
  [BriefDeliverableTaskTypeEnum.Brief]: {
    label: "Brief",
    description: "Initial brief & campaign requirements",
    type: BriefDeliverableTaskTypeEnum.Brief,
  },
  [BriefDeliverableTaskTypeEnum.Script]: {
    label: "Script",
    description: "Script creation & review",
    type: BriefDeliverableTaskTypeEnum.Script,
  },
  [BriefDeliverableTaskTypeEnum.Draft]: {
    label: "Draft",
    description: "Video / Draft content creation & review",
    type: BriefDeliverableTaskTypeEnum.Draft,
  },
  [BriefDeliverableTaskTypeEnum.Live]: {
    label: "Live",
    description: "Final post & live publishing",
    type: BriefDeliverableTaskTypeEnum.Live,
  },
};

const getStageIcon = (taskType?: BriefDeliverableTaskTypeEnum) => {
  switch (taskType) {
    case BriefDeliverableTaskTypeEnum.Brief:
      return <FileText size={14} />;
    case BriefDeliverableTaskTypeEnum.Script:
      return <ScrollText size={14} />;
    case BriefDeliverableTaskTypeEnum.Draft:
      return <Video size={14} />;
    case BriefDeliverableTaskTypeEnum.Live:
      return <Radio size={14} />;
    default:
      return <FileText size={14} />;
  }
};

export function CampaignTimeline({
  deliverableId,
  onClose,
}: {
  deliverableId: string;
  onClose?: () => void;
}) {
  const firstRender = useRef(true);
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [renameValue, setRenameValue] = useState("");
  const [addingStageType, setAddingStageType] =
    useState<BriefDeliverableTaskTypeEnum | null>(null);

  const { briefId } = useBriefStore();

  const { config: deliverableConfigs = [], isFetching } =
    useGetBriefDeliverableConfig(briefId);

  const { saveDeliverable, isSaving, isSaved } =
    useSaveDeliverableConfig(briefId);
  const { deleteTask, isDeleting, isDeleted } =
    useDeleteBriefConfigTask(briefId);
  const {
    renameDeliverable,
    isRenaming: isRenamingLoading,
    isRenamed,
  } = useRenameBriefConfigDeliverable(briefId);
  const { createTask, isCreating, isCreated } =
    useCreateBriefConfigTask(briefId);
  const { editTask, isEditing, isEdited } = useEditBriefConfigTask(briefId);
  const { reorderTask, isReordering } = useReorderBriefConfigTask(briefId);

  useEffect(() => {
    if (deliverableId && firstRender.current) {
      firstRender.current = false;
      return setSelectedConfigId(deliverableId);
    }
    if (deliverableConfigs.length > 0 && !selectedConfigId && !deliverableId) {
      setSelectedConfigId(deliverableConfigs[0].deliverableConfigRefId);
    }
  }, [deliverableConfigs, selectedConfigId, deliverableId]);

  const selectedConfig = useMemo(() => {
    return (
      deliverableConfigs.find(
        (config) => config.deliverableConfigRefId === selectedConfigId
      ) || deliverableConfigs[0]
    );
  }, [deliverableConfigs, selectedConfigId]);

  useEffect(() => {
    if (selectedConfig) {
      setRenameValue(selectedConfig.name);
    }
  }, [selectedConfig]);

  useEffect(() => {
    if (isSaved) {
      ToastService.success("Deliverable saved successfully");
      onClose?.();
    }
  }, [isSaved]);

  useEffect(() => {
    if (isRenamed) {
      ToastService.success("Deliverable Renamed");
      setIsEditingName(false);
    }
  }, [isRenamed]);

  useEffect(() => {
    if (isDeleted) ToastService.success("Stage removed");
  }, [isDeleted]);

  useEffect(() => {
    if (isCreated) {
      ToastService.success("Stage added successfully");
      setAddingStageType(null);
    }
  }, [isCreated]);

  useEffect(() => {
    if (isEdited) ToastService.success("Timeline updated");
  }, [isEdited]);

  const activeTasks = selectedConfig?.tasks || [];
  const activeDeliverableName = selectedConfig?.name || "Deliverable";

  const existingTypes = new Set(activeTasks.map((t) => t.type));
  const removedDefaultStages = Object.values(STAGE_META).filter(
    (meta) => !existingTypes.has(meta.type)
  );

  const hasBrandApprover = (task: IBriefDeliverableTaskConfig): boolean => {
    return (
      task.approvalType?.includes(UploaderType.Brand) ||
      task.approvalType?.includes("Brand" as UploaderType)
    );
  };

  const handleToggleBrandApprover = useCallback(
    (task: IBriefDeliverableTaskConfig, checked: boolean) => {
      if (!selectedConfig) return;

      const currentApprovals = task.approvalType || [];
      const isTaskLive = task.type === BriefDeliverableTaskTypeEnum.Live;

      // Checking if approvals have "Brand". If toggled off, remove all approvals by clearing the array.
      const nextApprovalType: ApprovalType[] = isTaskLive
        ? []
        : checked
          ? currentApprovals.includes(UploaderType.Brand)
            ? currentApprovals
            : [...currentApprovals, UploaderType.Brand]
          : [];

      const updates: Partial<IBriefDeliverableTaskConfig> = {
        name: task.name,
        instructions: "",
        uploaderType:
          task.uploaderType && task.uploaderType.length > 0
            ? task.uploaderType
            : [UploaderType.Brand],
        approvalType: nextApprovalType,
      };

      if (task.deadline) updates.deadline = task.deadline;

      editTask({
        data: {
          briefId,
          deliverableConfigRefId: selectedConfig.deliverableConfigRefId,
          taskId: task.taskId,
          updates,
        },
      });
    },
    [selectedConfig, editTask, briefId]
  );

  const handleAssigneeChange = useCallback(
    (task: IBriefDeliverableTaskConfig, newUploaders: UploaderType[]) => {
      if (!selectedConfig) return;

      const updates: Partial<IBriefDeliverableTaskConfig> = {
        name: task.name,
        instructions: "",
        uploaderType:
          newUploaders && newUploaders.length > 0
            ? newUploaders
            : [UploaderType.Brand],
        approvalType: task.approvalType || [],
        isApprovalRequired: task.isApprovalRequired || false,
      };

      if (task.deadline) updates.deadline = task.deadline;

      editTask({
        data: {
          briefId,
          deliverableConfigRefId: selectedConfig.deliverableConfigRefId,
          taskId: task.taskId,
          updates,
        },
      });
    },
    [selectedConfig, editTask, briefId]
  );

  const handleDeleteTask = (taskId: string) => {
    if (selectedConfig) {
      deleteTask({
        briefId,
        deliverableConfigRefId: selectedConfig.deliverableConfigRefId,
        taskId,
      });
    }
  };

  const handleAddBackStage = (stageType: BriefDeliverableTaskTypeEnum) => {
    if (!selectedConfig) return;
    const meta = STAGE_META[stageType];

    setAddingStageType(stageType);

    const newTaskPayload: any = {
      name: meta.label,
      instructions: "",
      uploaderType: [UploaderType.Operations],
      approvalType: [],
      isApprovalRequired: false,
      type: stageType,
      taskId: `temp-${Date.now()}`,
    };

    createTask({
      data: {
        briefId,
        deliverableConfigRefId: selectedConfig.deliverableConfigRefId,
        task: newTaskPayload as IBriefDeliverableTaskConfig,
      },
    });
  };

  /* Deliverable renaming functionality - commented out
  const handleSaveRename = () => {
    if (selectedConfig && renameValue.trim()) {
      renameDeliverable({
        data: {
          briefId,
          deliverableConfigRefId: selectedConfig.deliverableConfigRefId,
          name: renameValue.trim(),
          description: selectedConfig.description,
        },
      });
    }
  };

  const handleCancelRename = () => {
    setRenameValue(selectedConfig?.name || "");
    setIsEditingName(false);
  };
  */

  const handleApplyToAllDeliverables = () => {
    if (!selectedConfig) return;
    deliverableConfigs.forEach((config) => {
      if (
        config.deliverableConfigRefId !== selectedConfig.deliverableConfigRefId
      ) {
        config.tasks.forEach((task) => {
          const sourceTask = selectedConfig.tasks.find(
            (t) => t.type === task.type
          );
          if (sourceTask) {
            const isTaskLive = task.type === BriefDeliverableTaskTypeEnum.Live;
            const updates: Partial<IBriefDeliverableTaskConfig> = {
              name: task.name,
              instructions: "",
              uploaderType:
                task.uploaderType && task.uploaderType.length > 0
                  ? task.uploaderType
                  : [UploaderType.Brand],
              approvalType: isTaskLive ? [] : sourceTask.approvalType || [],
            };

            if (task.deadline) updates.deadline = task.deadline;

            editTask({
              data: {
                briefId,
                deliverableConfigRefId: config.deliverableConfigRefId,
                taskId: task.taskId,
                updates,
              },
            });
          }
        });
      }
    });
    ToastService.success("Applied to all deliverables");
  };

  if (isFetching) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Deliverable header label & stage count subtitle */}
      <div className={styles.deliverablesHeaderRow}>
        <span className={styles.deliverablesLabel}>Deliverable</span>
        <span className={styles.stageCountSubtitle}>
          {activeTasks.length} OF {Object.keys(STAGE_META).length} STAGES ·{" "}
          {activeDeliverableName}
        </span>
      </div>

      {/* Deliverables selector pills */}
      {deliverableConfigs.length > 0 && (
        <div className={styles.deliverablePills}>
          {deliverableConfigs.map((config) => {
            const selected =
              selectedConfig?.deliverableConfigRefId ===
              config.deliverableConfigRefId;

            /* Deliverable rename input - commented out
            if (selected && isEditingName) {
              return (
                <Input
                  key={config.deliverableConfigRefId}
                  size="small"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveRename();
                    if (e.key === "Escape") handleCancelRename();
                  }}
                  style={{ width: "200px" }}
                  addonAfter={
                    <Flex align="center" gap="2px">
                      <Button
                        onClick={handleCancelRename}
                        icon={<XCircleIcon fill="#FF3B30" weight="fill" />}
                        size="small"
                        type="text"
                      />
                      <Button
                        onClick={handleSaveRename}
                        icon={<CheckCircleIcon fill="#34C759" weight="fill" />}
                        loading={isRenamingLoading}
                        size="small"
                        type="text"
                      />
                    </Flex>
                  }
                />
              );
            }
            */

            return (
              <button
                key={config.deliverableConfigRefId}
                type="button"
                onClick={() => {
                  setSelectedConfigId(config.deliverableConfigRefId);
                  setIsEditingName(false);
                }}
                className={`${styles.deliverablePill} ${
                  selected ? styles.active : ""
                }`}
              >
                <span>{config.name}</span>
                <span className={styles.countBadge}>{config.tasks.length}</span>
                {/* Deliverable rename button - commented out
                {selected && (
                  <Button
                    icon={<EditOutlined />}
                    type="text"
                    size="small"
                    style={{
                      padding: 0,
                      marginLeft: 2,
                      height: "auto",
                      color: selected ? "#ffffff" : "#4f566b",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditingName(true);
                    }}
                  />
                )}
                */}
              </button>
            );
          })}
        </div>
      )}

      {/* Body */}
      <div className={styles.body}>
        <p className={styles.helperText}>
          Stages apply only to{" "}
          <span className={styles.highlight}>{activeDeliverableName}</span>.
          Script and Draft include brand approval by default.
        </p>

        {/* Active Stages */}
        <div className={styles.stagesList}>
          {activeTasks.map((task, index) => {
            const isBrandApproved = hasBrandApprover(task);
            const isLastTask = activeTasks.length <= 1;

            return (
              <div key={task.taskId} className={styles.stageCard}>
                <div className={styles.stageCardHeader}>
                  <div className={styles.iconWrapper}>
                    {getStageIcon(task.type)}
                  </div>
                  <div className={styles.stageInfo}>
                    <div className={styles.stageTitleRow}>
                      <span className={styles.stageIndex}>
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <h5 className={styles.stageName}>{task.name}</h5>
                    </div>
                    <p className={styles.stageDescription}>
                      {STAGE_META[task.type]?.description ||
                        task.description ||
                        task.instructions ||
                        `${task.name} stage`}
                    </p>
                  </div>

                  {task.type !== BriefDeliverableTaskTypeEnum.Brief && (
                    <Tooltip
                      title={
                        isLastTask
                          ? "Keep at least one stage"
                          : `Remove ${task.name}`
                      }
                    >
                      <Button
                        type="text"
                        danger
                        icon={<Trash2 size={14} />}
                        disabled={isLastTask || isDeleting}
                        size="small"
                        style={{ color: "#a3acb9" }}
                      />
                    </Tooltip>
                  )}
                </div>

                {/* <div className={styles.assigneeRow}>
                  <div className={styles.assigneeLabelArea}>
                    <div className={styles.assigneeTitle}>
                      <User size={12} className={styles.icon} />
                      Assignee
                    </div>
                    <span className={styles.assigneeStatus}>
                      Who uploads/completes this stage
                    </span>
                  </div>

                  <Select
                    mode="multiple"
                    size="small"
                    value={task.uploaderType || [UploaderType.Operations]}
                    disabled={isEditing}
                    onChange={(values) =>
                      handleAssigneeChange(task, values as UploaderType[])
                    }
                    options={[
                      { label: "Operations", value: UploaderType.Operations },
                      { label: "Brand", value: UploaderType.Brand },
                    ]}
                    maxTagCount="responsive"
                    style={{ minWidth: 180 }}
                  />
                </div> */}

                <div className={styles.approverRow}>
                  <div className={styles.approverLabelArea}>
                    <div className={styles.approverTitle}>
                      <UserCheck size={12} className={styles.icon} />
                      Brand as approver
                    </div>
                    <span className={styles.approverStatus}>
                      {isBrandApproved
                        ? "Brand must approve this stage"
                        : "Internal review only"}
                    </span>
                  </div>

                  <Switch
                    checked={isBrandApproved}
                    loading={isEditing}
                    onChange={(checked) =>
                      handleToggleBrandApprover(task, checked)
                    }
                    aria-label={`Brand as approver for ${task.name}`}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Removed Stages Section */}
        {removedDefaultStages.length > 0 && (
          <div className={styles.removedSection}>
            <h4 className={styles.removedTitle}>Removed stages</h4>
            <div className={styles.removedList}>
              {removedDefaultStages.map((meta) => (
                <div key={meta.type} className={styles.removedItem}>
                  <div className={styles.removedItemLabel}>
                    <span className={styles.icon}>
                      {getStageIcon(meta.type)}
                    </span>
                    <span>{meta.label}</span>
                  </div>
                  <Button
                    type="text"
                    icon={<Plus size={14} />}
                    loading={isCreating && addingStageType === meta.type}
                    onClick={() => handleAddBackStage(meta.type)}
                    style={{
                      color: "#635bff",
                      fontWeight: 700,
                      fontSize: "12px",
                    }}
                  >
                    Add back
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Utilities */}
        {/* <div className={styles.utilitiesRow}>
          {deliverableConfigs.length > 1 && (
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={handleApplyToAllDeliverables}
              style={{ color: "#635bff", fontWeight: 600, fontSize: "12px", padding: 0 }}
            >
              Apply to all deliverables
            </Button>
          )}
        </div> */}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <Button
          type="primary"
          size="large"
          block
          loading={isSaving}
          disabled={isSaving}
          onClick={() => {
            if (selectedConfig) {
              saveDeliverable({
                data: {
                  briefId,
                  deliverableConfigRefId: selectedConfig.deliverableConfigRefId,
                },
              });
            } else if (onClose) {
              onClose();
            }
          }}
          style={{
            backgroundColor: "#635bff",
            borderRadius: "8px",
            fontWeight: 700,
            fontSize: "13px",
            width: "100%",
          }}
        >
          Done
        </Button>
      </div>
    </div>
  );
}
